import { Theme, ListItem, ListItemText, useTheme, Box, Grid } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { Tooltip } from "@mui/material";

/** [[CrewScheduleEventSummarList]] Properties */
interface ICrewScheduleEventSummaryProps {
  /** Array of items and subheaders to display in the list */
  items: Array<ISummaryItem | ISummarySubHeader>;
  /** Title to use for the summary list, defaults to 'Summary' */
  summaryTitle?: string;
  /** Is the summary disabled? */
  disabled?: boolean;
  /** Should the title be hidden? */
  hideTitle?: boolean;
  /** Hide description (when used in context menu use special component for this) */
  hideDescription?: boolean;
}

/** This is how we expect items to be expressed to us */
export interface ISummaryItem {
  /** The key to reference this particular item */
  key: string;
  /** Data to display */
  value: string | number | JSX.Element;
  /** A visually appealing way of displaying the Key */
  label?: string | JSX.Element;
}

/** Format for a subheader in the summary list */
export interface ISummarySubHeader {
  /** Text to display in the subheader */
  text: string;
}

/**
 * Summary List for Crew Schedule events, for the context menu.
 * @param props - [[CrewScheduleEventSummaryList.ICrewScheduleEventSummaryProps]] Properties
 */
const CrewScheduleEventSummaryList = (props: ICrewScheduleEventSummaryProps): JSX.Element => {
  const { items, disabled, summaryTitle, hideTitle, hideDescription } = props;

  const theme: Theme = useTheme();

  // If disabled we don't need to map the list or anything.
  if (disabled) {
    return (
      <Box>
        <ListItem>
          <ListItemText secondary="Summary Not Available" />
        </ListItem>
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={0}
      sx={{
        minWidth: "280px",
        maxWidth: "350px",
      }}
    >
      {!hideTitle && (
        <Grid item xs={12} sx={{ fontWeight: 900, py: 1, px: 2 }}>
          {summaryTitle ?? "Summary"}
        </Grid>
      )}
      {items.map((summItem: ISummaryItem | ISummarySubHeader, index: number) => {
        if (!summItem) return null;
        if (!(summItem as ISummarySubHeader).text) {
          const item: ISummaryItem = summItem as ISummaryItem;
          const warning = item?.key?.includes("markedForDeletion");
          return hideDescription && item?.label === "Description" ? null : (
            // TODO: Fix string conversion in key at item.value
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            <Grid container columnSpacing={1} key={`${item.key}-${item.value}-${index}`}>
              <Grid
                xs={4}
                item
                sx={{
                  display:
                    !item.hasOwnProperty("label") ||
                    (typeof item?.label === "string" && item?.label.replace(" ", "") === "")
                      ? "none"
                      : "inline-block",
                  fontSize: "0.8em",
                  fontWeight: 900,
                  textAlign: "right",
                }}
              >
                {item?.label}
              </Grid>
              <Grid
                xs={8}
                item
                sx={{
                  fontSize: "0.8em",
                  ml:
                    !item.hasOwnProperty("label") ||
                    (typeof item?.label === "string" && item?.label.replace(" ", "") === "")
                      ? 2
                      : 0,
                }}
              >
                {item?.value}

                {warning && (
                  <Tooltip title="Missing APIS Information" placement="top">
                    <ErrorIcon className="missing_icon" color="error" />
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          );
        }
        const header: ISummarySubHeader = summItem as ISummarySubHeader;
        return (
          <Grid item xs={12} sx={{ fontWeight: 900, py: 1, px: 2 }} key={`${header.text}`}>
            {header.text}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CrewScheduleEventSummaryList;
